import React from "react";
import { Box, Typography } from "@mui/material";
import { shallow } from "zustand/shallow";

import { decodeHtmlEntities, getTwoDigitNo } from "utils";
import useStore, { defaults } from "components/PerformanceReview/store";
import {
  CategoryMap,
  InterviewQuestionTypes,
  SubCategoryMap,
} from "components/PerformanceReview/constants";

import QuestionNav from "./QuestionNav";
import QuestionCard from "./QuestionCard";
import ParameterSection from "./ParameterSection";

/**
 * "Question specific feedback" (mobile) — mobile port of desktop's big
 * `PerformanceReview/Analysis` component.
 *
 * Header + question pills + question/response/feedback card + the Speech and
 * Content parameter sections + the example response. The Grammar section from
 * the Figma is intentionally omitted (no backing data — desktop only analyses
 * Speech + Content).
 */

const defaultTagState = {
  RELEVANCE: [{ category: "RELEVANCE", subCategory: "JARGON_WORDS", value: [] }],
  WORD_CHOICE: [
    { category: "WORD_CHOICE", subCategory: "INFORMAL_WORDS", value: [] },
    { category: "WORD_CHOICE", subCategory: "RARE_USED_WORDS", value: [] },
  ],
};

const commParametersOrder = ["FLUENCY", "CONFIDENCE", "DELIVERY", "WORD_CHOICE"];

/** Ports desktop `CategoriesAnalysis`: splits a question into speech + content parameter rows. */
function buildCategories(question) {
  const src =
    question?.isError || !question
      ? defaults.questions[0]
      : question;

  const {
    communication: { ratings: _comm = {}, tags: commTags = [] } = {},
    content: { ratings: _cont = {}, tags: contTags = [] } = {},
  } = src;

  const commRatings = { ..._comm };
  const contRatings = { ..._cont };
  const overall = {
    speech: commRatings.OVERALL || 0,
    content: contRatings.OVERALL || 0,
  };
  delete commRatings.OVERALL;
  delete contRatings.OVERALL;

  const contTagMap = (contTags || []).reduce((acc, t) => {
    acc[t.category] = acc[t.category] ? [...acc[t.category], { ...t }] : [{ ...t }];
    return acc;
  }, {});

  let content = Object.keys(contRatings).map((key) => ({
    category: key,
    score: contRatings[key],
    tags:
      key === CategoryMap.RELEVANCE
        ? contTagMap[key] || defaultTagState.RELEVANCE
        : null,
  }));

  const commTagMap = (commTags || []).reduce((acc, t) => {
    acc[t.category] = acc[t.category] ? [...acc[t.category], { ...t }] : [{ ...t }];
    return acc;
  }, {});

  const wordChoiceData = JSON.parse(JSON.stringify(defaultTagState.WORD_CHOICE));
  (commTagMap.WORD_CHOICE || []).forEach((tag) => {
    if (tag.subCategory === SubCategoryMap.INFORMAL_WORDS) wordChoiceData[0].value = tag.value;
    if (tag.subCategory === SubCategoryMap.RARE_USED_WORDS) wordChoiceData[1].value = tag.value;
  });

  let communication = Object.keys(commRatings).map((key) => ({
    category: key,
    score: commRatings[key],
    tags:
      key === CategoryMap.CONFIDENCE
        ? null
        : key === CategoryMap.WORD_CHOICE
        ? wordChoiceData
        : commTagMap[key] || [],
  }));

  // Situational questions can't be scored on Similarity — desktop drops it.
  if (question?.type === InterviewQuestionTypes[1]._id) {
    content = content.filter((d) => d.category !== "SIMILARITY");
  }

  communication.sort(
    (a, b) =>
      commParametersOrder.indexOf(a.category) - commParametersOrder.indexOf(b.category)
  );

  return { communication, content, overall };
}

export default function QuestionFeedback() {
  const questions = useStore((s) => s.questions, shallow);
  const currentQuestion = useStore((s) => s.currentQuestion);
  const activeQuestion = questions[currentQuestion];

  const { communication, content, overall } = React.useMemo(
    () => buildCategories(activeQuestion),
    [activeQuestion]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Header */}
      <Box>
        <Typography variant="h6-semiBold" color="neutral.clr-800">
          Question specific feedback
        </Typography>
        <Typography variant="body02-regular" color="neutral.clr-500" sx={{ mt: 1, display: "block" }}>
          Find detailed feedback to memorize for improvement
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body01-semiBold" color="neutral.clr-800">
          Total questions -
        </Typography>
        <Typography variant="body01-semiBold" color="primary.clr-300">
          {getTwoDigitNo(questions.length)}
        </Typography>
      </Box>

      <QuestionNav />

      <QuestionCard />

      {!activeQuestion?.isError && (
        <>
          <ParameterSection parameter="Speech" featureData={communication} overall={overall.speech} />
          <ParameterSection parameter="Content" featureData={content} overall={overall.content} />
        </>
      )}

      <SampleAnswer question={activeQuestion} />
    </Box>
  );
}

/** "Example Response" / "Ideal Response" — mobile port of desktop `SampleAnswerForm`. */
function SampleAnswer({ question }) {
  const isSituational = question?.type === "situational";

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "tertiary.clr-300",
        bgcolor: "tertiary.clr-100",
        borderRadius: 3,
        p: 4,
      }}
    >
      <Typography variant="h6-semiBold" color="primary.clr-300">
        {isSituational ? "Example Response" : "Ideal Response"}
      </Typography>
      <Typography
        variant="body01-regular"
        color="neutral.clr-900"
        sx={{ mt: 1, display: "block" }}
        dangerouslySetInnerHTML={{
          __html: decodeHtmlEntities(question?.expectedAnswer || ""),
        }}
      />
    </Box>
  );
}
